import { useCallback, useMemo, useRef, useState } from 'react';
import { HSLColor } from '@yola/yola-palette-generator';
import cloneDeep from 'lodash.clonedeep';
import supportedColorModes from '../constants/supported-color-modes';
import gradientBaseColorTypes from '../constants/gradient-base-color-types';
import buildGradientModelFromSolidColorCSSValue from '../helpers/build-gradient-model-from-solid-color-css-value';
import buildGradientModelByString from '../helpers/build-gradient-model-by-string';
import {
  buildSolidColorFromGradientCSSValue,
  buildSolidColorFromGradientModel,
} from '../helpers/build-solid-color-from-gradient';
import getResolvedColorAlphaValue from '../helpers/get-resolved-color-alpha-value';
import parseHslColorString from '../helpers/parse-hsl-color-string';
import parseHslaColorString from '../helpers/parse-hsla-color-string';
import deriveGradientBaseColorFromGradientModel from '../helpers/derive-gradient-base-color-from-gradient-model';

const COLOR_MODE_STATE_SHARED_DATA_ID = 'websiteDesignColorModeState';

const wrapSharedDataById = (colorModeState) => ({
  [COLOR_MODE_STATE_SHARED_DATA_ID]: colorModeState,
});

const getInternalSharedData = (sharedData) => sharedData?.[COLOR_MODE_STATE_SHARED_DATA_ID] || null;

const removeInternalSharedData = (sharedData) => {
  const { [COLOR_MODE_STATE_SHARED_DATA_ID]: _, ...nextSharedData } = sharedData || {};

  return nextSharedData;
};

const getInitialColorMode = ({ gradientCSSValue }) =>
  gradientCSSValue && gradientCSSValue !== 'none'
    ? supportedColorModes.GRADIENT
    : supportedColorModes.SOLID;

const cloneGradientColorModel = (gradientColorModel) =>
  gradientColorModel ? cloneDeep(gradientColorModel) : null;

const cloneColorHSLA = (solidColorHSLA) => (solidColorHSLA ? { ...solidColorHSLA } : null);

const getNormalizedHslaColor = (color, opacity) => ({
  ...parseHslColorString(color),
  a: getResolvedColorAlphaValue(opacity),
});

const getInitialGradientModel = ({ restoredGradientColorModel, gradientCSSValue }) => {
  const nextInitialGradientModel = cloneGradientColorModel(restoredGradientColorModel);

  if (nextInitialGradientModel) {
    return nextInitialGradientModel;
  }

  if (!gradientCSSValue || gradientCSSValue === 'none') {
    return null;
  }

  const parsedGradientColorModel = buildGradientModelByString(gradientCSSValue);

  if (!parsedGradientColorModel) {
    return null;
  }

  return parsedGradientColorModel;
};

const getInitialGradientColorModel = ({ restoredInitialGradientColorModel, gradientCSSValue }) =>
  cloneGradientColorModel(restoredInitialGradientColorModel) ||
  getInitialGradientModel({
    restoredGradientColorModel: null,
    gradientCSSValue,
  });

const isGradientBaseColorDifferentFromAuto = (gradientColorModel, gradientBaseColorHSLA) => {
  const gradientAutoBaseColorHSLA = deriveGradientBaseColorFromGradientModel(gradientColorModel);

  if (!gradientAutoBaseColorHSLA || !gradientBaseColorHSLA) {
    return false;
  }

  return (
    gradientBaseColorHSLA.h !== gradientAutoBaseColorHSLA.h ||
    gradientBaseColorHSLA.s !== gradientAutoBaseColorHSLA.s ||
    gradientBaseColorHSLA.l !== gradientAutoBaseColorHSLA.l
  );
};

const useColorModeState = ({ sharedData = null, initialColor, opacity, gradientCSSValue }) => {
  const restoredInternalSharedData = useMemo(() => getInternalSharedData(sharedData), [sharedData]);

  const {
    colorMode: restoredColorMode = null,
    gradientColorModel: restoredGradientColorModel = null,
    initialGradientColorModel: restoredInitialGradientColorModel = null,
    solidColorHSLA: restoredSolidColorHSLA = null,
    initialSolidColorHSLA: restoredInitialSolidColorHSLA = null,
    isFirstSwitch: restoredIsFirstSwitch = true,
    gradientCustomBaseColorHSLA: restoredGradientCustomBaseColorHSLA = null,
  } = restoredInternalSharedData || {};

  const resolvedInitialColorMode =
    restoredColorMode ||
    getInitialColorMode({
      gradientCSSValue,
    });

  const initialSolidColorHSLA = useMemo(
    () =>
      cloneColorHSLA(restoredInitialSolidColorHSLA) ||
      getNormalizedHslaColor(initialColor, opacity),
    [initialColor, opacity, restoredInitialSolidColorHSLA]
  );
  const initialGradientColorModel = useMemo(
    () =>
      getInitialGradientColorModel({
        restoredInitialGradientColorModel,
        gradientCSSValue,
      }),
    [gradientCSSValue, restoredInitialGradientColorModel]
  );
  const initialGradientBaseColorHSLA = useMemo(
    () => getNormalizedHslaColor(initialColor, 1),
    [initialColor]
  );

  const [colorMode, setColorMode] = useState(() => restoredColorMode || resolvedInitialColorMode);
  const [solidColorHSLA, setSolidColorHSLA] = useState(
    () => cloneColorHSLA(restoredSolidColorHSLA) || cloneColorHSLA(initialSolidColorHSLA)
  );

  const [gradientColorModel, setGradientColorModel] = useState(() =>
    getInitialGradientModel({
      restoredGradientColorModel,
      gradientCSSValue,
    })
  );
  const [gradientCustomBaseColorHSLA, setGradientCustomBaseColorHSLA] = useState(() => {
    if (restoredInternalSharedData) {
      return cloneColorHSLA(restoredGradientCustomBaseColorHSLA);
    }

    return isGradientBaseColorDifferentFromAuto(
      initialGradientColorModel,
      initialGradientBaseColorHSLA
    )
      ? cloneColorHSLA(initialGradientBaseColorHSLA)
      : null;
  });
  const gradientBaseColorHSLA = useMemo(
    () =>
      gradientCustomBaseColorHSLA ||
      deriveGradientBaseColorFromGradientModel(gradientColorModel) ||
      initialGradientBaseColorHSLA,
    [gradientCustomBaseColorHSLA, gradientColorModel, initialGradientBaseColorHSLA]
  );

  const isFirstSwitchRef = useRef(Boolean(restoredIsFirstSwitch));

  const updateSolidColorHSLA = useCallback((nextSolidColorHSLA) => {
    setSolidColorHSLA(nextSolidColorHSLA);
  }, []);

  const updateGradientColorModel = useCallback((nextGradientColorModel) => {
    setGradientColorModel(cloneGradientColorModel(nextGradientColorModel));
  }, []);

  const setGradientBaseColorTypeToAuto = useCallback(() => {
    setGradientCustomBaseColorHSLA(null);
  }, []);

  const getGradientBaseColorType = useCallback(
    () =>
      gradientCustomBaseColorHSLA ? gradientBaseColorTypes.CUSTOM : gradientBaseColorTypes.AUTO,
    [gradientCustomBaseColorHSLA]
  );

  const handleColorModeChange = useCallback(
    (nextMode) => {
      let nextSolidColorHSLA = solidColorHSLA;
      let nextGradientModel = gradientColorModel;

      if (nextMode === supportedColorModes.SOLID) {
        if (resolvedInitialColorMode === supportedColorModes.GRADIENT && isFirstSwitchRef.current) {
          const solidColorCSSValue =
            buildSolidColorFromGradientModel(gradientColorModel, true) ||
            buildSolidColorFromGradientCSSValue(gradientCSSValue, true);

          if (solidColorCSSValue) {
            nextSolidColorHSLA = parseHslaColorString(solidColorCSSValue);
            updateSolidColorHSLA(nextSolidColorHSLA);
          }
        }

        nextGradientModel = null;
      }

      if (nextMode === supportedColorModes.GRADIENT) {
        if (!gradientColorModel) {
          const solidColorCSSValue = new HSLColor(
            ...Object.values(nextSolidColorHSLA)
          ).toCSSValue();
          nextGradientModel = buildGradientModelFromSolidColorCSSValue(solidColorCSSValue);

          if (nextGradientModel) {
            updateGradientColorModel(nextGradientModel);
          }
        }

        nextSolidColorHSLA = gradientBaseColorHSLA;
      }

      isFirstSwitchRef.current = false;

      setColorMode(nextMode);

      return {
        nextSolidColorHSLA,
        nextGradientModel,
      };
    },
    [
      solidColorHSLA,
      gradientColorModel,
      resolvedInitialColorMode,
      gradientCSSValue,
      updateSolidColorHSLA,
      gradientBaseColorHSLA,
      updateGradientColorModel,
    ]
  );

  const buildSharedDataPatch = useCallback(
    (overrides = {}) => {
      const nextColorModeState = {
        colorMode,
        initialSolidColorHSLA,
        initialGradientColorModel,
        isFirstSwitch: isFirstSwitchRef.current,
        solidColorHSLA,
        gradientColorModel,
        gradientCustomBaseColorHSLA,
        ...overrides,
      };

      return wrapSharedDataById(nextColorModeState);
    },
    [
      colorMode,
      gradientCustomBaseColorHSLA,
      gradientColorModel,
      initialSolidColorHSLA,
      initialGradientColorModel,
      solidColorHSLA,
    ]
  );

  return {
    colorMode,
    initialSolidColorHSLA,
    initialGradientColorModel,
    solidColorHSLA,
    updateSolidColorHSLA,
    gradientColorModel,
    updateGradientColorModel,
    gradientBaseColorHSLA,
    getGradientBaseColorType,
    setGradientBaseColorTypeToAuto,
    handleColorModeChange,
    buildSharedDataPatch,
    removeInternalSharedData,
  };
};

export default useColorModeState;
